// REACT IMPORTS
import { TickCircleIcon } from "icons/auth/TickCircleIcon";
import { useEffect, useState } from "react";
import SuccessTick from "icons/success/tick.gif";
// LIBRARY IMPORTS
import { Button as ButtonField } from "react-aria-components";
import { useAppSelector } from "store/hooks";
import { adjustColorForHover, getDisabledColor } from "utils/HelperFunctions";
import { RootState } from "store/store";

// PROPS TYPE
interface ButtonProps {
  text: string;
  type?: "button" | "reset" | "submit" | undefined;
  className?: string;
  isDisabled?: boolean;
  onClick?: any;
  variant?: "primary" | "white" | "stroke-outline" | "secondary" | "variable";
  isLoading?: boolean;
  icon?: any;
  width?: string;
  img?: any;
  right?: string;
  isSuccess?: boolean;
  isRightLoader?: boolean;
  isInteractionDisabled?: boolean;
  "data-testid"?: string;
  "aria-label"?: string;
}

/* ------------------------------------- BUTTON ------------------------------------- */

const Button = (props: ButtonProps) => {
  const {
    text = "Submit",
    type = "button",
    className = "",
    isDisabled = false,
    onClick,
    variant = "variable",
    isLoading = false,
    icon = null,
    width,
    img = null,
    right,
    isSuccess = false,
    isRightLoader = false,
    isInteractionDisabled = false,
    ...buttonProps
  } = props;
  const { accentColor } = useAppSelector((state: RootState) => state.dashboard);
  const [backgroundColor, setBackgroundColor] = useState(accentColor);

  useEffect(() => {
    setBackgroundColor(accentColor);
  }, [accentColor]);

  const styles: any = {
    primary: `${
      isDisabled
        ? "bg-btnPrimaryInactive cursor-not-allowed"
        : `bg-btnPrimaryActive hover:bg-btnPrimaryHover ${
            isLoading || isSuccess ? "cursor-not-allowed" : "cursor-pointer"
          }`
    } text-white`,
    secondary: `${
      isDisabled
        ? "bg-bgPrimary text-txtSecondaryInActive cursor-not-allowed"
        : `bg-btnOutlineActive hover:bg-bgHover text-txtParagraph !border-strokePrimary !border ${
            isLoading || isSuccess ? "cursor-not-allowed" : "cursor-pointer"
          }`
    } text-btnPrimaryActive`,
    white: `${
      isDisabled
        ? "bg-bgPrimary text-txtSecondaryInActive cursor-not-allowed"
        : `bg-btnOutlineActive hover:bg-bgHover text-txtParagraph !border-strokePrimary !border ${
            isLoading || isSuccess ? "cursor-not-allowed" : "cursor-pointer"
          }`
    } text-btnPrimaryActive`,
    "stroke-outline": `${
      isDisabled
        ? "bg-btnOutlineInactive cursor-not-allowed"
        : `bg-btnOutlineActive hover:bg-btnOutlineHover ${
            isLoading || isSuccess ? "cursor-not-allowed" : "cursor-pointer"
          }`
    } text-txtParagraph border border-strokePrimary`,
    variable: isDisabled
      ? "cursor-not-allowed text-white"
      : `${
          isLoading || isSuccess ? "cursor-not-allowed" : "cursor-pointer"
        } text-white`,
  };

  const Icons: any = {
    SuccessTick: (
      <img src={SuccessTick} alt="SuccessIcon" className="h-12 w-auto" />
    ),
    TickCircleIcon: <TickCircleIcon className="otp-success-tick" />,
  };

  return (
    <ButtonField
      isDisabled={isDisabled || isLoading || isSuccess || isInteractionDisabled}
      style={{
        width: width ? width : "",
        backgroundColor:
          variant === "variable"
            ? isDisabled
              ? getDisabledColor(accentColor)
              : backgroundColor
            : "",
        borderWidth: "1px",
        borderColor:
          variant === "variable"
            ? isDisabled
              ? getDisabledColor(accentColor)
              : backgroundColor
            : "",
      }}
      className={`${styles[variant]} px-2 lg:px-4 ${
        isSuccess || (icon && icon === "SuccessTick")
          ? "lg:py-0 py-0"
          : "lg:py-2.5 py-1.5 gap-2"
      } rounded-md text-base leading-6 font-medium focus:outline-none flex items-center  justify-center ${right} ${className} `}
      type={type}
      onPress={onClick}
      {...buttonProps}
      onHoverChange={(isHovering: boolean) => {
        if (!isDisabled && variant === "variable") {
          const backgroundColor = isHovering
            ? adjustColorForHover(accentColor)
            : accentColor;
          setBackgroundColor(backgroundColor);
        }
      }}
    >
      {isLoading && !isRightLoader && (
        <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
      )}
      {img && !isSuccess ? img : null}
      {isSuccess ? (
        <img src={SuccessTick} alt="SuccessIcon" className="h-12 w-auto" />
      ) : (
        text
      )}
      {isLoading && isRightLoader && (
        <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
      )}
      {icon ? Icons[icon] : null}
    </ButtonField>
  );
};

export default Button;
